import React, { useCallback, useState } from 'react';
import { GoogleMap, useLoadScript, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { Campaign } from '../models/Campaign';

interface CampaignMapViewProps {
    campaigns: Campaign[];
    onCampaignSelect?: (campaignId: string) => void;
}

const mapContainerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '12px'
};

const defaultCenter = {
    lat: 50.0, // Centered on southern Canada where most population/campaigns are
    lng: -105.0  // Keep the same longitude to show both coasts
};

const defaultZoom = 3; // Keep the same zoom level

const CampaignMapView: React.FC<CampaignMapViewProps> = ({ campaigns, onCampaignSelect }) => {
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: 'AIzaSyC1yRdGkAtur1HeriWgfzmm2fwlK8W7_00',
        libraries: ['places']
    });

    const mapRef = React.useRef<google.maps.Map>();
    const onMapLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
        
        // Set the default view regardless of markers
        map.setCenter(defaultCenter);
        map.setZoom(defaultZoom);
        
        // Only adjust bounds if we have campaigns with coordinates that are outside view
        const campaignsWithCoords = campaigns.filter(
            campaign => campaign.latitude && campaign.longitude
        );
        
        if (campaignsWithCoords.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            campaignsWithCoords.forEach(campaign => {
                bounds.extend({ lat: campaign.latitude!, lng: campaign.longitude! });
            });
            
            // Only adjust bounds if markers are outside current view
            const currentBounds = map.getBounds();
            if (!currentBounds || !campaignsWithCoords.every(campaign => 
                currentBounds.contains({ lat: campaign.latitude!, lng: campaign.longitude! })
            )) {
                map.fitBounds(bounds);
                
                // After fitting bounds, ensure we're not too zoomed out
                google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                    const zoom = map.getZoom();
                    if (zoom && zoom < defaultZoom) {
                        map.setZoom(defaultZoom);
                        map.setCenter(defaultCenter);
                    }
                });
            }
        }
    }, [campaigns]);

    if (loadError) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#FEF2F2' }}>
                <Typography color="error">
                    Error loading maps
                </Typography>
            </Paper>
        );
    }

    if (!isLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={defaultZoom}
                center={defaultCenter}
                onLoad={onMapLoad}
                options={{
                    styles: [
                        {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }]
                        }
                    ],
                    fullscreenControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    zoomControl: true,
                    minZoom: 2,
                    maxZoom: 18,
                    restriction: {
                        latLngBounds: {
                            north: 80, // Reduced Arctic coverage but still visible
                            south: 35, // Well below southern Ontario
                            west: -150, // Full western coverage
                            east: -45,  // Full eastern coverage
                        },
                        strictBounds: false
                    }
                }}
            >
                {campaigns.map((campaign) => {
                    if (!campaign.latitude || !campaign.longitude) return null;
                    
                    return (
                        <MarkerF
                            key={campaign.id}
                            position={{
                                lat: campaign.latitude,
                                lng: campaign.longitude
                            }}
                            onClick={() => {
                                setSelectedCampaign(campaign);
                                if (onCampaignSelect && campaign.id) {
                                    onCampaignSelect(campaign.id);
                                }
                            }}
                        />
                    );
                })}

                {selectedCampaign && selectedCampaign.latitude && selectedCampaign.longitude && (
                    <InfoWindowF
                        position={{
                            lat: selectedCampaign.latitude,
                            lng: selectedCampaign.longitude
                        }}
                        onCloseClick={() => setSelectedCampaign(null)}
                    >
                        <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {selectedCampaign.centre_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCampaign.city}
                            </Typography>
                            {selectedCampaign.category && (
                                <Typography variant="body2" color="primary">
                                    {selectedCampaign.category}
                                </Typography>
                            )}
                        </Box>
                    </InfoWindowF>
                )}
            </GoogleMap>
        </Box>
    );
};

export default CampaignMapView; 